import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { useFunnelTimeComparison, type FunnelFilters, type TimeComparisonRow } from '@/hooks/useFunnelMetrics';

const STAGE_LABELS: Record<string, string> = {
  new: 'Novo',
  contact: 'Contato',
  scheduled: 'Reunião Agendada',
  proposal: 'Proposta',
  negotiation: 'Negociação',
};

const STAGE_ORDER = ['new', 'contact', 'scheduled', 'proposal', 'negotiation'];

interface Props {
  filters: FunnelFilters;
}

export default function FunnelTimeComparison({ filters }: Props) {
  const { data, isLoading } = useFunnelTimeComparison(filters);

  // Group data by stage
  const groupedByStage = STAGE_ORDER.map(stage => {
    const won = data?.find(r => r.stage === stage && r.outcome === 'won');
    const lost = data?.find(r => r.stage === stage && r.outcome === 'lost');
    const diff = (won?.avg_days || 0) - (lost?.avg_days || 0);
    return { stage, won, lost, diff };
  }).filter(row => row.won || row.lost);

  // Insight: find the biggest difference
  const biggestDiff = groupedByStage.reduce((max, row) => 
    Math.abs(row.diff) > Math.abs(max.diff) ? row : max, 
    { stage: '', diff: 0, won: undefined as TimeComparisonRow | undefined, lost: undefined as TimeComparisonRow | undefined }
  );

  const getDiffColor = (diff: number) => {
    if (diff > 5) return 'text-red-500';
    if (diff > 2) return 'text-yellow-500';
    if (diff < -2) return 'text-green-500';
    return 'text-muted-foreground';
  };

  const getDiffIcon = (diff: number) => {
    if (diff > 2) return <TrendingUp className="h-4 w-4" />;
    if (diff < -2) return <TrendingDown className="h-4 w-4" />;
    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Tempo por Estágio: Won vs Lost
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Tempo por Estágio: Won vs Lost
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {groupedByStage.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">Sem dados para o período selecionado</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estágio</TableHead>
                  <TableHead className="text-right text-green-600">Won (dias)</TableHead>
                  <TableHead className="text-right text-red-500">Lost (dias)</TableHead>
                  <TableHead className="text-right">Diferença</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedByStage.map(row => (
                  <TableRow key={row.stage}>
                    <TableCell className="font-medium">{STAGE_LABELS[row.stage] || row.stage}</TableCell>
                    <TableCell className="text-right">
                      {row.won ? (
                        <span className="text-green-600">{row.won.avg_days.toFixed(1)}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.lost ? (
                        <span className="text-red-500">{row.lost.avg_days.toFixed(1)}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className={`text-right flex items-center justify-end gap-1 ${getDiffColor(row.diff)}`}>
                      {getDiffIcon(row.diff)}
                      {row.diff > 0 ? '+' : ''}{row.diff.toFixed(1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Insight */}
            {biggestDiff.stage && Math.abs(biggestDiff.diff) > 2 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 text-sm">
                <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                <p>
                  <strong>Insight:</strong> Deals que fecham levam{' '}
                  {biggestDiff.diff > 0 ? (
                    <span className="text-green-600">{biggestDiff.diff.toFixed(1)} dias a mais</span>
                  ) : (
                    <span className="text-red-500">{Math.abs(biggestDiff.diff).toFixed(1)} dias a menos</span>
                  )}{' '}
                  no estágio <strong>{STAGE_LABELS[biggestDiff.stage]}</strong> comparado aos perdidos.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
